/**
 * Higienização do HTML dos cards.
 *
 * O conteúdo vem de importações antigas do Anki e de editores diversos, por
 * isso traz `<script>`, `onerror=`, `<iframe>` e `javascript:` — não por
 * malícia, mas porque nunca foi filtrado. Como é injectado com
 * `dangerouslySetInnerHTML`, tem de passar por aqui primeiro.
 *
 * A lista é de permissão, não de bloqueio: qualquer etiqueta ou atributo que
 * não esteja listado desaparece. É a única forma de isto continuar seguro
 * quando alguém importar um formato novo.
 */

const TAGS_PERMITIDAS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U', 'S', 'SUB', 'SUP', 'MARK', 'SMALL',
  'P', 'BR', 'HR', 'DIV', 'SPAN', 'PRE', 'CODE', 'BLOCKQUOTE',
  'UL', 'OL', 'LI', 'DL', 'DT', 'DD',
  'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TH', 'TD', 'CAPTION',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'IMG', 'A', 'FIGURE', 'FIGCAPTION',
]);

const ATRIBUTOS_PERMITIDOS: Record<string, Set<string>> = {
  IMG: new Set(['src', 'alt', 'width', 'height', 'title']),
  A: new Set(['href', 'title']),
  TD: new Set(['colspan', 'rowspan']),
  TH: new Set(['colspan', 'rowspan', 'scope']),
};

/** Etiquetas cujo texto interior nunca deve sobreviver ao desembrulho. */
const SEM_DESEMBRULHO = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'NOSCRIPT', 'TITLE', 'XMP']);

const HTML_NS = 'http://www.w3.org/1999/xhtml';

const ESQUEMAS_SEGUROS = /^(https?:|data:image\/(png|jpe?g|gif|webp|avif);base64,|\/)/i;

export function sanitizeCardHtml(html: string): string {
  if (!html) return '';

  const doc = new DOMParser().parseFromString(
    `<div id="mc-raiz">${html}</div>`,
    'text/html',
  );
  const raiz = doc.getElementById('mc-raiz');
  if (!raiz) return '';

  removerComentarios(raiz);
  limpar(raiz);
  return raiz.innerHTML;
}

/**
 * Comentários não acrescentam nada a um flashcard e são a matéria-prima do
 * mXSS: `<!--><img src=x onerror=...>` sobrevive à limpeza porque durante a
 * primeira leitura ainda faz parte do comentário, e volta a ser markup quando
 * o `innerHTML` é reinterpretado pelo navegador.
 */
function removerComentarios(raiz: Element): void {
  const doc = raiz.ownerDocument;
  const percurso = doc.createTreeWalker(raiz, NodeFilter.SHOW_COMMENT);
  const encontrados: Comment[] = [];
  while (percurso.nextNode()) encontrados.push(percurso.currentNode as Comment);
  for (const comentario of encontrados) comentario.remove();
}

function limpar(no: Element): void {
  // Percorrido de trás para a frente porque substituir um nó altera a lista.
  for (let i = no.children.length - 1; i >= 0; i -= 1) {
    const filho = no.children[i];
    if (!filho) continue;

    // Dentro de `<svg>` e `<math>` o `tagName` preserva a caixa original, por
    // isso `<svg><script>` chega aqui como `script` minúsculo. Normalizar é o
    // que impede esse script de escapar pelo ramo do desembrulho.
    const etiqueta = filho.tagName.toUpperCase();

    if (!TAGS_PERMITIDAS.has(etiqueta) || filho.namespaceURI !== HTML_NS) {
      // Conteúdo executável ou fora do namespace HTML sai inteiro; as restantes
      // cedem o lugar ao conteúdo, para não se perder texto por causa de um
      // `<font>`.
      if (SEM_DESEMBRULHO.has(etiqueta) || filho.namespaceURI !== HTML_NS) {
        filho.remove();
      } else {
        limpar(filho);
        filho.replaceWith(...Array.from(filho.childNodes));
      }
      continue;
    }

    const permitidos = ATRIBUTOS_PERMITIDOS[etiqueta];
    for (const atributo of Array.from(filho.attributes)) {
      const nome = atributo.name.toLowerCase();
      if (!permitidos?.has(nome)) {
        filho.removeAttribute(atributo.name);
        continue;
      }
      if (
        (nome === 'src' || nome === 'href') &&
        !ESQUEMAS_SEGUROS.test(atributo.value.trim())
      ) {
        filho.removeAttribute(atributo.name);
      }
    }

    if (etiqueta === 'A') {
      filho.setAttribute('target', '_blank');
      filho.setAttribute('rel', 'noopener noreferrer nofollow');
    }
    if (etiqueta === 'IMG') {
      filho.setAttribute('loading', 'lazy');
      filho.setAttribute('decoding', 'async');
    }

    limpar(filho);
  }
}

/**
 * Texto puro do card, para a leitura em voz alta e para as listagens.
 *
 * `textContent` sozinho traria o código de dentro de `<script>` e `<style>`,
 * que o TTS leria em voz alta.
 */
export function textoSimples(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  for (const no of Array.from(doc.body.querySelectorAll('script, style, template, noscript'))) {
    no.remove();
  }
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}
